'use client';
import * as React from 'react';

import { usePathname } from 'next/navigation';

import { useTranslation } from '@/lib/i18n/i18n.client';

import Audio from '@/components/atoms/Audio/Audio';
import Heading from '@/components/atoms/Heading';
import LinkButton from '@/components/atoms/LinkButton';
import Section from '@/components/atoms/Section/Section';
import { Text } from '@/components/atoms/Text';
import { Video } from '@/components/atoms/Video';
import PostByLine from '@/features/blog/PostByLine';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';
import { slugBlogAudioFileMap } from '@/utils/audio';

import { PostTags, PostTopic } from './PostTags';

import { SvgIcon } from '@/components/SvgIcon';
import iconChevronLeftSrc from '~/svg/IconChevronLeft.svg';
import BlogAISummarize from './BlogAISummarize';
import ShareStrip from './Sharing/ShareStrip';

export type BlogMetaDataProps = {
  image?: React.ReactNode;
  releaseDate: string;
  timeSpentReading?: number;
  isNew?: boolean;
  title?: string;
  video?: string;
  description?: React.ReactNode;
  topics?: PostTopic[];
  author?: {
    image?: React.ReactNode;
    name?: string;
  };
};

export default function BlogMetaData({
  image,
  releaseDate,
  timeSpentReading,
  title,
  video,
  description,
  topics,
  author,
  isNew,
}: BlogMetaDataProps) {
  const hasHeroImage = !!image;
  const hasHeroVideo = !!video;
  const hasHeroAsset = !!image || !!video;
  const hasPostTags = isNew || Number(topics?.length) > 0;
  const { texts } = useTranslation();

  const pathname = usePathname();
  const blogAudioTrack = (() => {
    const slug = pathname?.split('/').pop();

    if (!slug) return null;

    const availableAudioFile =
      slugBlogAudioFileMap[slug as keyof typeof slugBlogAudioFileMap];

    if (!availableAudioFile) return null;

    return availableAudioFile;
  })();

  return (
    <Section
      className='w-full px-5 !pt-0 pb-8 md:px-10 md:pb-[40px]'
      data-tracker-section={SECTION_NAMES.BLOG_METADATA}
      data-tracker-component='actions'
    >
      <LinkButton
        href='/blog'
        variant='text'
        color='secondary'
        leftSection={<SvgIcon src={iconChevronLeftSrc} />}
        underline={false}
        classNames={{
          root: 'text-gray-950 hover:text-base-black h-5 text-body-l leading-5 px-0',
        }}
      >
        {texts.blog.back_to_blog}
      </LinkButton>
      <div className='pt-12'>
        {hasPostTags && (
          <PostTags isNew={isNew ?? false} topics={topics ?? []} />
        )}
      </div>

      <div className='grid pt-3'>
        <Heading level='h1'>{title}</Heading>
        <div className='flex justify-between items-center gap-2 w-full mt-6'>
          <BlogAISummarize />
          <ShareStrip title={title ?? ''} />
        </div>
        {blogAudioTrack && <Audio audioTrack={blogAudioTrack} />}
        {!!description && (
          <Text
            as='div'
            variant='subtitle-m'
            className='text-body-l sm:text-body-l pt-6'
          >
            {description}
          </Text>
        )}
      </div>
      <div>
        <div className='flex items-start justify-between pt-6	sm:items-center'>
          <PostByLine
            releaseDate={releaseDate}
            timeSpentReading={timeSpentReading}
            author={author}
          />
        </div>

        {hasHeroAsset && (
          <div className='border-b border-gray-200 pb-8 pt-8 sm:pb-[2.625rem] sm:pt-9'>
            {hasHeroImage && !hasHeroVideo && (
              <div className='w-full rounded'>{image} </div>
            )}
            {hasHeroVideo && (
              <Video classNames={{ container: 'rounded' }} url={video} />
            )}
          </div>
        )}
      </div>
    </Section>
  );
}
